import axiosInstance from 'Api';
import configureStore from 'config/configureStore';
import { useApiFetcherSWR } from "hooks/SwrCustom";
import { setAuthToken } from 'reducers/UserProfile';
import useSWR from "swr";

const login = (username, password, rememberToken) => {
    return axiosInstance.post("auth/login",
        {
            username,
            password,
            rememberToken
        });
};

const logout = () => {
    return axiosInstance.post("/logout");
};


/** Soft AFAS menu role — stored in USER_ROLES like other session roles. */
export const AFAS_ROLE = 'afas';

const getUserRoles = () => {
    return JSON.parse(localStorage.getItem("USER_ROLES"));
};

const getCurrentUser = () => {
    return JSON.parse(localStorage.getItem("user"));
};

const setToken = (token) => {
    localStorage.setItem('REACT_TOKEN_AUTH', JSON.stringify(token));
    configureStore.dispatch(setAuthToken(token));
}

const setRefreshToken = (token) => {
    localStorage.setItem('REFRESH_TOKEN_AUTH', JSON.stringify(token));
}

const setUser = (user) => {
    localStorage.setItem('user', JSON.stringify(user));
}

const normalizeRoleOrder = (roles) => {
    const cleanedRoles = [...new Set((roles || []).filter(Boolean))];

    return cleanedRoles.sort((a, b) => {
        if (a === 'coordinator') return -1;
        if (b === 'coordinator') return 1;
        if (a === AFAS_ROLE) return 1;
        if (b === AFAS_ROLE) return -1;
        return 0;
    });
};

/**
 * Merge AFAS access into the roles array (additive role, never primary).
 * When canAccessAfas is null, keep whatever afas role is already present.
 */
const rolesWithAfasAccess = (roles, canAccessAfas = null) => {
    const base = (Array.isArray(roles) ? roles : []).filter(
        (role) => role && role !== AFAS_ROLE,
    );

    const shouldHaveAfas =
        canAccessAfas === null
            ? (Array.isArray(roles) && roles.includes(AFAS_ROLE))
            : Boolean(canAccessAfas);

    if (shouldHaveAfas) {
        base.push(AFAS_ROLE);
    }

    return normalizeRoleOrder(base);
};

/**
 * Pick AFAS access from login / impersonation / Besturen mutation payloads.
 * Also accepts roles already containing "afas".
 */
const readCanAccessAfas = (body) => {
    if (!body || typeof body !== 'object') {
        return null;
    }

    if (Object.prototype.hasOwnProperty.call(body, 'canAccessAfas')) {
        return Boolean(body.canAccessAfas);
    }

    const nested = body.data && typeof body.data === 'object' ? body.data : null;
    if (nested && Object.prototype.hasOwnProperty.call(nested, 'canAccessAfas')) {
        return Boolean(nested.canAccessAfas);
    }

    if (Array.isArray(body.roles)) {
        return body.roles.includes(AFAS_ROLE);
    }

    return null;
};

const setUserRoles = (roles) => {
    if (!Array.isArray(roles)) return;

    const sortedRoles = normalizeRoleOrder(roles);
    localStorage.setItem('USER_ROLES', JSON.stringify(sortedRoles));
    localStorage.setItem(
        'CAN_ACCESS_AFAS',
        sortedRoles.includes(AFAS_ROLE) ? '1' : '0',
    );
};

/**
 * Persist session roles from an auth payload (login / impersonation / logout-impersonate).
 * Prefer canAccessAfas when present; otherwise trust roles[] already containing "afas".
 */
const applySessionRoles = (payload) => {
    const roles = Array.isArray(payload?.roles) ? payload.roles : [];
    const canAccessAfas = readCanAccessAfas(payload);
    const nextRoles = rolesWithAfasAccess(roles, canAccessAfas);
    setUserRoles(nextRoles);
    return nextRoles;
};

const getCanAccessAfas = () => {
    const roles = getUserRoles();
    if (Array.isArray(roles)) {
        return roles.includes(AFAS_ROLE);
    }

    const stored = localStorage.getItem('CAN_ACCESS_AFAS');
    if (stored === null) {
        return false;
    }
    return stored === '1';
};

const setCanAccessAfas = (canAccess) => {
    const nextRoles = rolesWithAfasAccess(getUserRoles() || [], Boolean(canAccess));
    setUserRoles(nextRoles);
    return nextRoles;
};

/**
 * When Besturen add/remove affects the currently logged-in admin, sync the afas role.
 * Pass canAccessAfas from the API when available (correct if user remains on other orgs).
 */
const syncCurrentUserAfasRole = (targetAdminId, canAccessAfas) => {
    const current = getCurrentUser();
    if (!current || Number(current.iAdminId) !== Number(targetAdminId)) {
        return null;
    }
    if (canAccessAfas === null || canAccessAfas === undefined) {
        return null;
    }
    return setCanAccessAfas(Boolean(canAccessAfas));
};

export const saveToken = (data) => {
    const token = JSON.parse(localStorage.getItem("REACT_TOKEN_AUTH"));
    !token && setToken(data.token.access_token);
}

const makeAuth = (data) => {
    if (data.token && data.token.access_token) {
        setRefreshToken(data.token.refresh_token);
        saveToken(data);
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${data.token.access_token}`;

        if (data.user) {
            setUser(data.user);
        }

        if (Array.isArray(data.roles) || readCanAccessAfas(data) !== null) {
            applySessionRoles(data);
        }
    }
}

// const checkUserLoggedIn = () => {
//     let checkUser = JSON.parse(localStorage.getItem("REACT_TOKEN_AUTH"));
//     if (checkUser) {
//         return true;
//     }
//     return false;
// }

const checkUserLoggedIn = () => {
    const token = JSON.parse(localStorage.getItem("REACT_TOKEN_AUTH"));
    const roles = JSON.parse(localStorage.getItem("USER_ROLES")) || [];

    if (!token) {
        return false;
    }

    const isAdmin = roles.includes("administrator");

    if (!isAdmin) {
        return true;
    }

    return localStorage.getItem("2fa_verified") === "true";
};

const regenerateRefreshToken = () => {
    return new Promise((resolve, reject) => {
        const refreshToken = JSON.parse(localStorage.getItem("REFRESH_TOKEN_AUTH"));

        if (refreshToken) {
            let postdata = {
                refresh_token: refreshToken
            }
            axiosInstance.post('auth/refresh', postdata).then((response) => {
                if (response.status != undefined && response.status == 200) {
                    setToken(response.data.access_token);
                    setRefreshToken(response.data.refresh_token);
                    resolve(true);
                } else {
                    localStorage.clear();
                    window.location = "/login";
                }
            }).catch((err) => {

            });
        } else {
            localStorage.clear();
            window.location = "/login";
        }
    });
}

const TwoFAVerify = (data) => {
    return axiosInstance.post('/2fa', data);
}

const AuthService = {
    AFAS_ROLE,
    login,
    logout,
    getCurrentUser,
    getUserRoles,
    setUserRoles,
    rolesWithAfasAccess,
    applySessionRoles,
    setCanAccessAfas,
    getCanAccessAfas,
    readCanAccessAfas,
    syncCurrentUserAfasRole,
    makeAuth,
    checkUserLoggedIn,
    regenerateRefreshToken,
    TwoFAVerify
};
export default AuthService;


export const API_ENDPOINT = {
 
    LOGOUT: {
        url: "/logout",
        method: "post",
    },
};

export const useLogout = () =>
    useApiFetcherSWR(API_ENDPOINT.LOGOUT);


// One shared SWR key for `me`, so every consumer gets the same request and the
// same response shape (the raw axios response). Revalidation also picks up
// server-side changes such as the AFAS allowlist without a page reload.
export const useMe = () => useSWR('me', () => axiosInstance.get('me'));